<template>
  <ClientOnly>
    <Auth
      :appearance="{
        theme: ThemeSupa,
        variables: {
          default: {
            colors: {
              brand: '#49a078',
            },
          },
        },
      }"
      :supabaseClient="supabaseClient"
      :providers="['google']"
      :theme="$colorMode.value"
      :view="props.view"
      :redirectTo="redirectTo"
      :options="{ captchaToken }"
      :localization="localization"
      @auth:submit="handleAuthSubmit"
      @auth:error="handleAuthError"
      @update:view="handleUpdateView"
    />
    <div v-show="hasResendConfirmation" class="flex flex-col items-center mt-4">
      <div class="text-sm text-gray-500">
        {{ $t("common.resendConfirmationEmailDescription") }}
      </div>
      <Button
        variant="link"
        @click="handleResendConfirmation"
        class="text-sm text-primary mt-2"
      >
        {{ $t("buttons.resendConfirmationEmail") }}
      </Button>
    </div>
    <div v-if="hasTurnstile" class="flex justify-center mt-4">
      <NuxtTurnstile v-model="captchaToken" ref="captchaRef" />
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { Auth } from "auth-ui-vue";
import { toast } from "vue-sonner";
const { t, tm } = useI18n();

const captchaToken = ref("");
const captchaRef = ref(null);
const props = defineProps({
  view: {
    type: String,
    default: "sign_in",
    validator: (value: string) => {
      return [
        "update_password",
        "sign_in",
        "sign_up",
        "magic_link",
        "forgotten_password",
      ].includes(value);
    },
  },
});
const $emits = defineEmits(["update:view"]);
const config = useRuntimeConfig();
const supabaseClient = useSupabaseClient();
const currentView = ref(props.view);
const currentEmail = ref("");
const hasResendConfirmation = ref(false);
const route = useRoute();
const redirectTo = computed(() => {
  switch (currentView.value) {
    case "forgotten_password":
      return `${config.public.siteUrl}/profile/update`;
    case "sign_up":
      if (route.query.redirect) {
        let redirectPath = `${config.public.siteUrl}/${(
          route.query.redirect as string
        ).replace(/^\//, "")}`;

        return redirectPath;
      }
    default:
      return undefined;
  }
});

const hasTurnstile = computed(() => {
  return ["sign_up", "sign_in", "forgotten_password"].includes(
    currentView.value
  );
});

const handleAuthSubmit = (data: any) => {
  hasResendConfirmation.value = false;
  currentEmail.value = data.email;
};

const handleAuthError = (error: Error) => {
  if (error?.code === "email_not_confirmed") {
    hasResendConfirmation.value = true;
  }

  captchaRef.value?.reset();
};

const handleUpdateView = (view: string) => {
  $emits("update:view", view);
  currentView.value = view;
};

const handleResendConfirmation = async () => {
  try {
    const { error } = await supabaseClient.auth.resend({
      type: "signup",
      email: currentEmail.value,
      options: {
        captchaToken: captchaToken.value,
      },
    });

    if (error) throw error;

    toast.success(t("announcements.resendConfirmationEmail"), {
      position: "top-right",
      duration: 5000,
    });
  } catch (error) {
    console.error("Error resending confirmation:", error);
    toast.error(t("announcements.failedToResendConfirmationEmail"), {
      position: "top-right",
      duration: 5000,
    });
  }
};

const localization = computed(() => {
  return {
    variables: {
      sign_up: {
        email_label: t("components.authentication.sign_up.email_label"),
        password_label: t("components.authentication.sign_up.password_label"),
        email_input_placeholder: t(
          "components.authentication.sign_up.email_input_placeholder"
        ),
        password_input_placeholder: t(
          "components.authentication.sign_up.password_input_placeholder"
        ),
        button_label: t("components.authentication.sign_up.button_label"),
        loading_button_label: t(
          "components.authentication.sign_up.loading_button_label"
        ),
        social_provider_text: tm(
          "components.authentication.sign_up.social_provider_text"
        ),
        link_text: t("components.authentication.sign_up.link_text"),
        confirmation_text: t(
          "components.authentication.sign_up.confirmation_text"
        ),
      },
      sign_in: {
        email_label: t("components.authentication.sign_in.email_label"),
        password_label: t("components.authentication.sign_in.password_label"),
        email_input_placeholder: t(
          "components.authentication.sign_in.email_input_placeholder"
        ),
        password_input_placeholder: t(
          "components.authentication.sign_in.password_input_placeholder"
        ),
        button_label: t("components.authentication.sign_in.button_label"),
        loading_button_label: t(
          "components.authentication.sign_in.loading_button_label"
        ),
        social_provider_text: tm(
          "components.authentication.sign_in.social_provider_text"
        ),
        link_text: t("components.authentication.sign_in.link_text"),
      },
      magic_link: {
        email_input_label: t(
          "components.authentication.magic_link.email_input_label"
        ),
        email_input_placeholder: t(
          "components.authentication.magic_link.email_input_placeholder"
        ),
        button_label: t("components.authentication.magic_link.button_label"),
        loading_button_label: t(
          "components.authentication.magic_link.loading_button_label"
        ),
        link_text: t("components.authentication.magic_link.link_text"),
        confirmation_text: t(
          "components.authentication.magic_link.confirmation_text"
        ),
      },
      forgotten_password: {
        email_label: t(
          "components.authentication.forgotten_password.email_label"
        ),
        password_label: t(
          "components.authentication.forgotten_password.password_label"
        ),
        email_input_placeholder: t(
          "components.authentication.forgotten_password.email_input_placeholder"
        ),
        button_label: t(
          "components.authentication.forgotten_password.button_label"
        ),
        loading_button_label: t(
          "components.authentication.forgotten_password.loading_button_label"
        ),
        link_text: t("components.authentication.forgotten_password.link_text"),
        confirmation_text: t(
          "components.authentication.forgotten_password.confirmation_text"
        ),
      },
      update_password: {
        password_label: t(
          "components.authentication.update_password.password_label"
        ),
        password_input_placeholder: t(
          "components.authentication.update_password.password_input_placeholder"
        ),
        button_label: t(
          "components.authentication.update_password.button_label"
        ),
        loading_button_label: t(
          "components.authentication.update_password.loading_button_label"
        ),
        confirmation_text: t(
          "components.authentication.update_password.confirmation_text"
        ),
      },
      verify_otp: {
        email_input_label: t(
          "components.authentication.verify_otp.email_input_label"
        ),
        email_input_placeholder: t(
          "components.authentication.verify_otp.email_input_placeholder"
        ),
        phone_input_label: t(
          "components.authentication.verify_otp.phone_input_label"
        ),
        phone_input_placeholder: t(
          "components.authentication.verify_otp.phone_input_placeholder"
        ),
        token_input_label: t(
          "components.authentication.verify_otp.token_input_label"
        ),
        token_input_placeholder: t(
          "components.authentication.verify_otp.token_input_placeholder"
        ),
        button_label: t("components.authentication.verify_otp.button_label"),
        loading_button_label: t(
          "components.authentication.verify_otp.loading_button_label"
        ),
      },
    },
  };
});
</script>
